import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bot, Building2, Check, ChevronLeft, ChevronRight, EyeOff, Pencil, Search, Trash2 } from "lucide-react";
import {
  useGetAgents,
  useGetProviders,
  useGetUserDetails,
} from "../../../api/queryHooks";
import { deleteAgent, getProviderOAuthUrl } from "../../../api/providers";
import UserAuthStore from "../../../store/userStore";
import UIStore from "../../../store/uiStore";
import ConfirmDialog from "../../shared/ConfirmDialog/ConfirmDialog";
import Loader from "../../Loader/Loader";
import Input from "../../Inputs/Input";
import Select from "../../Inputs/Select";
import AlertBanner from "../../shared/AlertBanner/AlertBanner";
import ViewToggle from "../../shared/ViewToggle";
import { ROUTE_HELPERS } from "../../../router/routes";
import {
  getProviderConnectionState,
  PROVIDER_CONNECTION_STATE,
} from "../forgeConnectionState";
import logger from "../../../services/Logger";
import AuthenticatedImage from "../../Common/AuthenticatedImage";

const PAGE_SIZE = 20;

const CONNECTION_FILTER_OPTIONS = [
  { value: "all", label: "All connections" },
  { value: PROVIDER_CONNECTION_STATE.CONNECTED, label: "Connected" },
  { value: PROVIDER_CONNECTION_STATE.READY, label: "Ready" },
  { value: PROVIDER_CONNECTION_STATE.ENABLE_REQUIRED, label: "Enable required" },
  { value: PROVIDER_CONNECTION_STATE.REAUTHENTICATION_REQUIRED, label: "Reconnect required" },
  { value: PROVIDER_CONNECTION_STATE.DISCONNECTED, label: "Not connected" },
];

const StoredImage = ({ filePath, orgId, alt, fallback: Fallback, className = "w-6 h-6" }) => {
  return (
    <AuthenticatedImage
      filePath={filePath}
      orgId={orgId}
      alt={alt}
      className={`${className} object-contain`}
      fallback={<Fallback className={`${className} text-ink-gray-500 dark:text-ink-gray-400`} />}
    />
  );
};

const AgentMarketplaceSection = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const orgAvailable = UIStore((state) => state.orgAvailable);
  const orgModeError = UIStore((state) => state.orgModeError);
  const isAdmin = orgAvailable === true;
  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const [connectionFilter, setConnectionFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const { data: agentsData, isLoading: agentsLoading } = useGetAgents(
    {
      org_id: orgId,
      uid: user?.uid,
      include_disabled: isAdmin,
    },
    // include_disabled depends on the admin flag, so wait for it to resolve.
    { enabled: !!orgId && orgAvailable !== null }
  );
  const { data: providers = [], isLoading: providersLoading } = useGetProviders(orgId);
  const { data: userDetails } = useGetUserDetails(orgId, user?.uid);

  const providerById = useMemo(
    () => Object.fromEntries(providers.map((provider) => [provider.provider_id, provider])),
    [providers]
  );

  const providerFilterOptions = useMemo(
    () => [
      { value: "all", label: "All providers" },
      ...providers.map((provider) => ({ value: provider.provider_id, label: provider.name })),
    ],
    [providers]
  );

  const agents = useMemo(() => agentsData?.agents || [], [agentsData]);
  const filteredAgents = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return agents.filter((agent) => {
      const provider = providerById[agent.provider];
      const connectionState = getProviderConnectionState(userDetails, provider);
      const searchable = [
        agent.name,
        agent.description,
        provider?.name,
        ...(Array.isArray(agent.triggers) ? agent.triggers : []),
      ].filter(Boolean).join(" ").toLowerCase();

      return (!normalizedSearch || searchable.includes(normalizedSearch))
        && (providerFilter === "all" || agent.provider === providerFilter)
        && (connectionFilter === "all" || connectionState === connectionFilter);
    });
  }, [agents, connectionFilter, providerById, providerFilter, search, userDetails]);
  const totalPages = Math.max(1, Math.ceil(filteredAgents.length / PAGE_SIZE));
  const paginatedAgents = useMemo(
    () => filteredAgents.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [currentPage, filteredAgents]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [connectionFilter, providerFilter, search]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const handleConnection = async (provider) => {
    if (!provider || !user?.uid || !orgId) return;
    const authorizationUrl = await getProviderOAuthUrl(provider.provider_id, user.uid, orgId, {
      returnTo: "forge_connections",
    });
    window.location.href = authorizationUrl;
  };

  const confirmDelete = async () => {
    if (!agentToDelete) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteAgent(agentToDelete.id);
      await queryClient.invalidateQueries({ queryKey: ["get-agents", { org_id: orgId }] });
      setAgentToDelete(null);
    } catch (error) {
      logger.error("AgentMarketplaceSection", "Failed to delete agent", {
        error: error.message,
        agentId: agentToDelete.id,
      });
      const responseData = error.response?.data;
      const dependentAgentIds = responseData?.dependent_agent_ids;
      setDeleteError(
        dependentAgentIds?.length
          ? `${responseData.error}: ${dependentAgentIds.join(", ")}`
          : responseData?.error || error.message || "Failed to delete agent."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  if (orgAvailable === null && orgModeError) {
    return (
      <AlertBanner>
        Could not determine your permissions, so agents cannot be listed. Reload the page to try again.
      </AlertBanner>
    );
  }

  // A disabled query reports isLoading=false, so wait on the admin flag explicitly.
  if (orgAvailable === null || agentsLoading || providersLoading) {
    return <Loader variant="dashboard" />;
  }

  return (
    <div>
      <ConfirmDialog
        open={!!agentToDelete}
        title="Delete Agent?"
        error={deleteError}
        confirmLabel="Delete"
        loadingLabel="Deleting..."
        isLoading={isDeleting}
        onCancel={() => {
          setAgentToDelete(null);
          setDeleteError(null);
        }}
        onConfirm={confirmDelete}
        backdropClose={!isDeleting}
      >
        Delete "{agentToDelete?.name}" and its agent files? Agents referenced by swarm agents cannot be deleted.
      </ConfirmDialog>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-copy-hover z-10" />
          <Input
            id="agent-marketplace-search"
            name="agent-marketplace-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search agents"
            aria-label="Search agents"
            className="!pl-9"
          />
        </div>
        <Select
          value={providerFilter}
          onChange={setProviderFilter}
          options={providerFilterOptions}
          placeholder="All providers"
          className="lg:w-56"
        />
        <Select
          value={connectionFilter}
          onChange={setConnectionFilter}
          options={CONNECTION_FILTER_OPTIONS}
          placeholder="All connections"
          className="lg:w-56"
        />
      </div>

      {agents.length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
            {filteredAgents.length} {filteredAgents.length === 1 ? "agent" : "agents"}
          </p>
          {filteredAgents.length > 0 && (
            <ViewToggle value={viewMode} onChange={setViewMode} />
          )}
        </div>
      )}

      {agents.length === 0 ? (
        <div className="border border-dashed border-edge-gray-300 dark:border-edge-panel-border rounded-lg px-6 py-14 text-center">
          <Bot className="w-8 h-8 mx-auto text-ink-gray-400 mb-3" />
          <p className="text-sm font-semibold text-ink-gray-900 dark:text-ink-gray-50">No agents available</p>
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-1">An administrator can add the first agent from Forge.</p>
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="py-14 text-center text-sm text-ink-gray-500 dark:text-ink-gray-400">No agents match these filters.</div>
      ) : (
        <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" : "space-y-2"}>
          {paginatedAgents.map((agent) => {
            const agentId = agent.id || agent.agent_id || agent.name;
            const provider = providerById[agent.provider];
            const primaryTrigger = Array.isArray(agent.triggers) ? agent.triggers[0] : null;
            const connectionState = getProviderConnectionState(userDetails, provider);
            const providerEnabled = provider?.enabled !== false;
            const cleanupFailed = agent.deletion_status === "cleanup_failed";
            const hidden = agent.enabled === false;
            const usable = providerEnabled && !cleanupFailed;
            const ready = connectionState === PROVIDER_CONNECTION_STATE.READY;
            const connected = connectionState === PROVIDER_CONNECTION_STATE.CONNECTED;
            const blockedLabel = cleanupFailed ? "Cleanup failed" : !providerEnabled ? "Unavailable" : null;

            if (viewMode === "list") {
              return (
                <article
                  key={agentId}
                  className={`photon-content-card flex min-h-[72px] items-center gap-3 rounded-lg border bg-fill-white px-3 py-2.5 dark:bg-fill-dialog-surface ${
                    usable
                      ? "border-edge-gray-200 dark:border-edge-panel-border"
                      : "border-edge-gray-200 dark:border-edge-panel-border opacity-65"
                  }`}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-edge-gray-200 bg-fill-white dark:border-edge-panel-border dark:bg-fill-deep-surface">
                    <StoredImage
                      filePath={agent.icon_path}
                      orgId={orgId}
                      alt=""
                      fallback={Bot}
                      className="w-6 h-6"
                    />
                  </div>

                  <div className="min-w-0 flex-1 sm:max-w-[220px]">
                    <div className="flex min-w-0 items-center gap-2">
                      <h3 className="truncate text-sm font-semibold text-ink-ink dark:text-ink-white" title={agent.name}>
                        {agent.name}
                      </h3>
                      {primaryTrigger && (
                        <span className="photon-accent-label photon-agent-handle photon-reference-tag hidden truncate rounded bg-fill-indigo-50 px-2 py-0.5 text-[10px] font-medium text-ink-indigo-800 dark:bg-fill-hex-312e81/50 dark:text-ink-hex-a5b4fc sm:inline">
                          {primaryTrigger}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex min-w-0 items-center gap-1.5">
                      <StoredImage
                        filePath={provider?.icon_path}
                        orgId={orgId}
                        alt=""
                        fallback={Building2}
                        className="w-3.5 h-3.5"
                      />
                      <span className="truncate text-[11px] text-ink-gray-500 dark:text-ink-gray-400">
                        {provider?.name || "Unavailable"}
                      </span>
                    </div>
                  </div>

                  <p className="hidden min-w-0 flex-1 truncate text-xs text-ink-gray-500 dark:text-ink-gray-400 md:block">
                    {agent.description || "No description available."}
                  </p>

                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    {hidden && (
                      <span className="hidden items-center gap-1 text-[11px] font-medium text-ink-gray-500 dark:text-ink-gray-400 lg:inline-flex">
                        <EyeOff className="w-3 h-3" />
                        Unpublished
                      </span>
                    )}
                    {blockedLabel ? (
                      <span className="text-[11px] font-medium text-ink-gray-500 dark:text-ink-gray-400">
                        {blockedLabel}
                      </span>
                    ) : connected ? (
                      <span className="hidden items-center gap-1 text-[11px] font-medium text-ink-emerald-700 dark:text-ink-emerald-400 sm:inline-flex">
                        <Check className="w-3 h-3" />
                        Connected
                      </span>
                    ) : !ready && usable ? (
                      <button
                        type="button"
                        onClick={() => handleConnection(provider)}
                        className="rounded-md bg-fill-primary px-2.5 py-1 text-[11px] font-medium text-ink-on-primary hover:bg-fill-primary-hover"
                      >
                        {connectionState === PROVIDER_CONNECTION_STATE.REAUTHENTICATION_REQUIRED
                          ? "Reconnect"
                          : connectionState === PROVIDER_CONNECTION_STATE.ENABLE_REQUIRED ? "Enable" : "Connect"}
                      </button>
                    ) : null}

                    {isAdmin && (
                      <div className="flex items-center gap-1 border-l border-edge-gray-200 pl-2 dark:border-edge-panel-border">
                        <button
                          type="button"
                          onClick={() => navigate(ROUTE_HELPERS.forge.editAgent(agentId))}
                          className="rounded-md p-1.5 text-ink-gray-500 hover:bg-fill-gray-100 hover:text-ink-primary dark:hover:bg-fill-dialog-border"
                          title={`Edit ${agent.name}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAgentToDelete({ id: agentId, name: agent.name });
                            setDeleteError(null);
                          }}
                          className="rounded-md p-1.5 text-ink-gray-500 hover:bg-fill-red-50 hover:text-ink-red-600 dark:hover:bg-fill-red-900/20"
                          title={`Delete ${agent.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              );
            }

            return (
              <article
                key={agentId}
                className={`photon-content-card relative flex min-h-[210px] flex-col rounded-lg border bg-fill-white dark:bg-fill-dialog-surface p-4 ${
                  usable
                    ? "border-edge-gray-200 dark:border-edge-panel-border"
                    : "border-edge-gray-200 dark:border-edge-panel-border opacity-65"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="w-9 h-9 shrink-0 rounded-md border border-edge-gray-200 dark:border-edge-panel-border flex items-center justify-center overflow-hidden bg-fill-white dark:bg-fill-deep-surface">
                      <StoredImage
                        filePath={agent.icon_path}
                        orgId={orgId}
                        alt=""
                        fallback={Bot}
                        className="w-6 h-6"
                      />
                    </div>
                    {primaryTrigger && (
                      <span className="photon-accent-label photon-agent-handle photon-reference-tag truncate rounded bg-fill-indigo-50 dark:bg-fill-hex-312e81/50 px-2 py-0.5 text-[10px] font-medium text-ink-indigo-800 dark:text-ink-hex-a5b4fc">
                        {primaryTrigger}
                      </span>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => navigate(ROUTE_HELPERS.forge.editAgent(agentId))}
                        className="p-1.5 rounded-md text-ink-gray-500 hover:text-ink-primary hover:bg-fill-gray-100 dark:hover:bg-fill-dialog-border"
                        title={`Edit ${agent.name}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAgentToDelete({ id: agentId, name: agent.name });
                          setDeleteError(null);
                        }}
                        className="p-1.5 rounded-md text-ink-gray-500 hover:text-ink-red-600 hover:bg-fill-red-50 dark:hover:bg-fill-red-900/20"
                        title={`Delete ${agent.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <h3 className="mt-3 truncate text-sm font-semibold text-ink-ink dark:text-ink-white" title={agent.name}>{agent.name}</h3>
                {hidden && (
                  <span className="mt-1.5 inline-flex w-fit items-center gap-1 rounded-full bg-fill-gray-100 dark:bg-fill-dialog-border px-2 py-0.5 text-[10px] font-medium text-ink-gray-500 dark:text-ink-gray-400">
                    <EyeOff className="w-3 h-3" />
                    Unpublished
                  </span>
                )}
                <div className="mt-1.5 flex items-center gap-2 min-w-0">
                  <StoredImage
                    filePath={provider?.icon_path}
                    orgId={orgId}
                    alt=""
                    fallback={Building2}
                    className="w-3.5 h-3.5"
                  />
                  <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400 truncate">
                    Provider · {provider?.name || "Unavailable"}
                  </span>
                </div>
                <p className="mt-2.5 text-xs leading-relaxed text-ink-gray-500 dark:text-ink-gray-400 line-clamp-2 flex-1">
                  {agent.description || "No description available."}
                </p>

                {(blockedLabel || !ready) && (
                  <div className="mt-3 pt-3 border-t border-edge-gray-200 dark:border-edge-panel-border flex items-center justify-end">
                    {blockedLabel ? (
                      <span className="text-[11px] font-medium text-ink-gray-500 dark:text-ink-gray-400">
                        {blockedLabel}
                      </span>
                    ) : connected ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-emerald-700 dark:text-ink-emerald-400">
                        <Check className="w-3 h-3" />
                        Connected
                      </span>
                    ) : usable ? (
                      <button
                        type="button"
                        onClick={() => handleConnection(provider)}
                        className="rounded-md bg-fill-primary px-2.5 py-1 text-[11px] font-medium text-ink-on-primary hover:bg-fill-primary-hover"
                      >
                        {connectionState === PROVIDER_CONNECTION_STATE.REAUTHENTICATION_REQUIRED
                          ? "Reconnect"
                          : connectionState === PROVIDER_CONNECTION_STATE.ENABLE_REQUIRED ? "Enable" : "Connect"}
                      </button>
                    ) : null}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {filteredAgents.length > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-1 rounded-md border border-edge-gray-300 dark:border-edge-panel-border px-3 py-1.5 text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-50 dark:hover:bg-fill-dialog-border disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Previous
          </button>
          <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            disabled={currentPage === totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-edge-gray-300 dark:border-edge-panel-border px-3 py-1.5 text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-50 dark:hover:bg-fill-dialog-border disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentMarketplaceSection;
